import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, Radio, HardHat, Wrench, Truck, Layers, ArrowUpRight,
  CheckCircle2, Send, Zap, Map, Award, Cable,
} from "lucide-react";
import { MaskLine, MaskLineInView, Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/components/SmoothScroll";
import Typewriter from "@/components/Typewriter";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  COMPANY, CORE_VALUES, SERVICE_GROUPS, FLEET, CAPABILITIES, WHY,
  TRUST_BADGES, COVERAGE, COMPLIANCE, PARTNERS, HOME_STATES, ACTIVE_STATES,
} from "@/lib/content";

const GROUP_ICONS = [Radio, Wrench, Layers];

/**
 * Hero headline rendered as SVG so we can fill the letters with a *single* US flag
 * that flows in the wind (feTurbulence + feDisplacementMap) while slowly drifting
 * horizontally. No background-clip:text (avoids Chrome bugs) and no overflow:hidden
 * masking (avoids tops of letters being clipped).
 *
 * viewBox is fixed 1000 x 900; SVG scales fluidly to the container width, so the
 * text stays visually identical across desktop / tablet / mobile.
 */
function FlagHeadline() {
  return (
    <svg
      className="block w-full select-none"
      viewBox="0 0 2400 1140"
      preserveAspectRatio="xMinYMid meet"
      aria-hidden="true"
      data-testid="hero-flag-svg"
    >
      <defs>
        <filter id="wcc-wind" x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence type="fractalNoise" baseFrequency="0.008 0.022" numOctaves="2" seed="6" result="noise">
            <animate attributeName="baseFrequency" dur="9s"
              values="0.008 0.022;0.014 0.03;0.010 0.025;0.008 0.022"
              repeatCount="indefinite" />
          </feTurbulence>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="22" xChannelSelector="R" yChannelSelector="G" />
        </filter>

        <mask id="wcc-text-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="2400" height="1140">
          <rect x="0" y="0" width="2400" height="1140" fill="black" />
          <g fill="white" style={{ fontFamily: "'Clash Display', 'Space Grotesk', system-ui, sans-serif" }}
             fontWeight="700" textAnchor="start">
            <text x="0" y="500" fontSize="540" letterSpacing="-18">WEST</text>
            <text x="0" y="890" fontSize="540" letterSpacing="-18">CORAL</text>
            <text x="0" y="1080" fontSize="230" textLength="2360" lengthAdjust="spacingAndGlyphs">COMMUNICATIONS</text>
          </g>
        </mask>
      </defs>

      <g mask="url(#wcc-text-mask)">
        <image
          href={`${process.env.PUBLIC_URL}/assets/us-flag.png`}
          xlinkHref={`${process.env.PUBLIC_URL}/assets/us-flag.png`}
          width="3000"
          height="1360"
          y="-110"
          preserveAspectRatio="xMidYMid slice"
          filter="url(#wcc-wind)"
        >
          <animate attributeName="x" values="-240;-820;-240" dur="26s" repeatCount="indefinite" />
        </image>
      </g>
    </svg>
  );
}

function Hero() {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  // Subtle parallax: as the user scrolls the first ~700px of the page, the flag
  // headline drifts DOWN by 120px relative to its natural position — visually,
  // it lags behind the hero video, creating a "depth" effect.
  const flagParallaxY = useTransform(scrollY, [0, 700], [0, 120]);

  return (
    <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-wcc-navy" data-testid="hero-section">
      <video
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay muted loop playsInline
        poster="/assets/field-1.webp"
        data-testid="hero-video"
      >
        <source src="/hero-video.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-wcc-navy via-wcc-navy/45 to-wcc-navy/10" />
      <div className="absolute inset-0 bg-wcc-navy/10" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(115%_75%_at_12%_100%,rgba(10,18,36,0.82),rgba(10,18,36,0.25)_45%,transparent_65%)]" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="flex items-start gap-3 mb-7 max-w-full"
        >
          <span className="h-px w-6 sm:w-10 bg-wcc-red shrink-0 mt-[0.55em]" />
          <span className="mono text-[10px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.3em] text-white/80 leading-relaxed min-w-0">
            Turnkey National Telecommunications Contractor
          </span>
        </motion.div>

        {/* Outer wrap = scroll parallax. Inner wrap = entrance animation. */}
        <motion.div style={{ y: flagParallaxY }} className="flag-wave-wrap w-full max-w-[880px] will-change-transform">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          >
            <h1 className="sr-only">West Coral Communications</h1>
            <FlagHeadline />
          </motion.div>
        </motion.div>

        <div className="mt-6 max-w-2xl">
          <p className="font-display text-lg sm:text-2xl text-white/90 tracking-tight">
            <Typewriter text="Building the future of connectivity — one line at a time." />
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <Link to="/careers" data-testid="hero-careers-btn"
            className="group inline-flex items-center gap-2 bg-wcc-red text-white font-semibold px-7 py-3.5 rounded-sm shadow-brut hover:bg-wcc-red-deep hover:-translate-y-0.5 transition-[transform,background-color] duration-200">
            Build Your Career <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
          <button onClick={() => scrollToId("contact")} data-testid="hero-contact-btn"
            className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white hover:text-wcc-navy transition-colors">
            Request Capabilities
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }}
          className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
        >
          {["Turnkey Underground Fiber", "FTTH", "Utility Infrastructure"].map((t) => (
            <span key={t} className="mono text-[11px] uppercase tracking-widest text-white/60">
              ▸ {t}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Overview() {
  return (
    <section id="about" className="relative bg-wcc-paper py-24 sm:py-32" data-testid="overview-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>01 — Who We Are</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tighter leading-[0.95] text-wcc-ink">
              <MaskLineInView>A large-scale, turnkey</MaskLineInView>
              <MaskLineInView delay={0.08}>telecommunications</MaskLineInView>
              <MaskLineInView delay={0.16} className="text-wcc-red">contractor.</MaskLineInView>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-14">
            <Reveal>
              <p className="text-lg sm:text-xl leading-relaxed text-wcc-ink/80">
                West Coral Communications is a nationwide turnkey telecommunications contractor. We self-perform
                underground fiber construction, directional and missile boring, plowing, trenching, restoration,
                and emergency response — backed by executive-level project management and experienced crews ready
                to mobilize anywhere in the country.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 border-l-2 border-wcc-red pl-6">
                <p className="mono text-[11px] uppercase tracking-[0.25em] text-wcc-gray mb-2">Our Mission</p>
                <p className="text-lg text-wcc-ink/90 leading-relaxed">
                  To deliver safe, dependable, high-quality telecommunications infrastructure at national scale —
                  and to be the one accountable partner our primes count on to get it done.
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Core values — numbered manifesto chapters */}
        <div className="mt-20 sm:mt-28">
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-8">Core Values</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-wcc-ink/10 border border-wcc-ink/10">
            {CORE_VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.06} className="bg-wcc-paper">
                <div className="group relative p-8 h-full hover:bg-white transition-colors">
                  <span className="ghost-number text-6xl block mb-6">{v.n}</span>
                  <h3 className="font-display text-xl font-medium text-wcc-ink mb-2">{v.t}</h3>
                  <p className="text-sm text-wcc-ink/60 leading-relaxed">{v.d}</p>
                  <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-wcc-red group-hover:w-full transition-all duration-500" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Field photograph — full-bleed feature under core values.
            Caption is stacked BELOW on mobile so it never covers the photo,
            and overlaid on desktop for cinematic effect. */}
        <Reveal delay={0.1}>
          <figure className="mt-20 sm:mt-28 relative overflow-hidden border border-wcc-ink/10" data-testid="core-values-image">
            <div className="relative">
              <img
                src="/assets/flags-trencher.webp"
                alt="Vermeer PTX44 trencher on a Kentucky field with rows of American flags flying behind — West Coral Communications"
                className="w-full h-auto object-contain block"
                style={{ aspectRatio: "1200 / 630" }}
                loading="lazy"
              />
              {/* Gradient scrim only on sm+ where caption overlays the image */}
              <div className="absolute inset-0 hidden sm:block bg-gradient-to-t from-wcc-navy/70 via-wcc-navy/10 to-transparent pointer-events-none" />
            </div>
            {/* Below-image caption on mobile, absolute overlay on sm+ */}
            <figcaption className="p-6 bg-wcc-navy text-white sm:absolute sm:left-0 sm:right-0 sm:bottom-0 sm:p-10 sm:bg-transparent">
              <p className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-white/70 mb-2">In the field</p>
              <p className="font-display text-lg sm:text-3xl font-semibold uppercase tracking-tight max-w-3xl leading-tight">
                Experienced self-performing crews. Nationwide mobilization. One accountable partner.
              </p>
            </figcaption>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="relative bg-white py-24 sm:py-32 border-y border-wcc-ink/10" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <Eyebrow>02 — Services</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tighter uppercase leading-none text-wcc-ink">
              Underground
              <br />
              <span className="text-outline">Construction</span>
            </h2>
          </div>
          <p className="max-w-sm text-wcc-ink/60 leading-relaxed">
            From plow to placement to closeout — a full-scope underground fiber and utility construction partner.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {SERVICE_GROUPS.map((g, gi) => {
            const Icon = GROUP_ICONS[gi];
            return (
              <Reveal key={g.title} delay={gi * 0.08}>
                <div className="group h-full bg-wcc-paper border border-wcc-ink/10 p-8 hover:border-wcc-red transition-colors">
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-12 h-12 grid place-items-center bg-wcc-navy text-white group-hover:bg-wcc-red transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="mono text-xs text-wcc-gray">0{gi + 1}</span>
                  </div>
                  <h3 className="font-display text-2xl font-medium text-wcc-ink mb-6">{g.title}</h3>
                  <ul className="space-y-2.5">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-wcc-ink/75">
                        <span className="mt-2 h-1.5 w-1.5 bg-wcc-red shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Equipment() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <>
      {/* Full-bleed hero photograph introducing the fleet — Ditch Witch RT120 Quad on our Kentucky yard.
          Caption stacks below the image on mobile so it never covers the photo. */}
      <Reveal>
        <figure className="relative overflow-hidden bg-wcc-navy" data-testid="fleet-hero-image">
          <div className="relative">
            <img
              src="/assets/rt120-quad.webp"
              alt="Ditch Witch RT120 Quad rubber-track tractor on the West Coral Communications yard in Kentucky"
              className="w-full h-auto object-cover block"
              style={{ aspectRatio: "2000 / 1200", maxHeight: "72vh", objectPosition: "center 30%" }}
              loading="lazy"
            />
            <div className="absolute inset-0 hidden sm:block bg-gradient-to-t from-wcc-navy/70 via-wcc-navy/10 to-transparent pointer-events-none" />
          </div>
          <figcaption className="p-6 bg-wcc-navy text-white sm:absolute sm:left-0 sm:right-0 sm:bottom-0 sm:p-10 sm:bg-transparent pointer-events-none">
            <p className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-white/70 mb-2">Fleet</p>
            <p className="font-display text-lg sm:text-3xl font-semibold uppercase tracking-tight max-w-3xl leading-tight">
              Modern, self-owned iron — ready to work, coast to coast.
            </p>
          </figcaption>
        </figure>
      </Reveal>

    <section id="equipment" ref={ref} className="relative bg-wcc-paper py-24 sm:py-32" data-testid="equipment-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <Eyebrow>03 — Fleet & Equipment</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter uppercase leading-none text-wcc-ink mb-8">
              Modern iron.
              <br />
              <span className="text-wcc-red">Real production.</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {FLEET.map((f, i) => (
                <Reveal key={f} delay={i * 0.03}>
                  <div className="flex items-center gap-3 py-2.5 border-b border-wcc-ink/10">
                    <Truck className="w-4 h-4 text-wcc-red shrink-0" />
                    <span className="text-wcc-ink/85">{f}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-10">
              <p className="mono text-[11px] uppercase tracking-[0.25em] text-wcc-gray mb-4">Capabilities</p>
              <div className="flex flex-wrap gap-2">
                {CAPABILITIES.map((c) => (
                  <span key={c} className="mono text-xs uppercase tracking-wider border border-wcc-ink/20 text-wcc-ink/80 px-3 py-1.5 hover:border-wcc-red hover:text-wcc-red transition-colors">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <motion.div style={{ y: y1 }} className="col-span-2 clip-notch overflow-hidden bg-wcc-navy aspect-[16/10] relative">
              <img src="/assets/rc120-tractor.webp" alt="Ditch Witch RC120 rubber-track tractor with backhoe on the West Coral Communications yard" className="w-full h-full object-cover object-center" />
              <span className="absolute bottom-4 left-4 mono text-[10px] uppercase tracking-widest text-white bg-wcc-red px-2 py-1">Utility Crossings</span>
            </motion.div>
            <motion.div style={{ y: y2 }} className="clip-notch overflow-hidden bg-wcc-navy aspect-[3/4] relative">
              <img src="/assets/field-3.webp" alt="Fiber line placement" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div style={{ y: y1 }} className="clip-notch overflow-hidden bg-wcc-navy aspect-[3/4] relative">
              <img src="/assets/field-2.webp" alt="Open trench along roadway" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

function Why() {
  return (
    <section className="relative bg-white py-24 sm:py-32 border-t border-wcc-ink/10" data-testid="why-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-16">
          <Eyebrow>04 — Why West Coral</Eyebrow>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-wcc-ink leading-[1.05]">
            <MaskLineInView>A subcontractor you can</MaskLineInView>
            <MaskLineInView delay={0.08}>confidently put in front</MaskLineInView>
            <MaskLineInView delay={0.16}>of your customers.</MaskLineInView>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-wcc-ink/10 border border-wcc-ink/10">
          {WHY.map((w, i) => (
            <Reveal key={w} delay={i * 0.05} className="bg-white">
              <div className="p-7 h-full flex items-start gap-3 hover:bg-wcc-paper transition-colors">
                <CheckCircle2 className="w-5 h-5 text-wcc-red shrink-0 mt-0.5" />
                <span className="text-wcc-ink/85 font-medium">{w}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PrimeReady() {
  return (
    <section id="prime" className="relative bg-wcc-navy text-white py-24 sm:py-32 overflow-hidden" data-testid="prime-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative z-10">
        <div className="max-w-4xl">
          <Eyebrow>05 — Prime Contractor Ready</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tighter uppercase leading-[0.95]">
            <MaskLineInView>Fully Insured.</MaskLineInView>
            <MaskLineInView delay={0.08}>Safety Compliant.</MaskLineInView>
            <MaskLineInView delay={0.16} className="text-wcc-red">Ready to Mobilize.</MaskLineInView>
          </h2>
          <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-2xl">
            We maintain insurance and compliance programs that meet the requirements of major utility companies,
            general contractors, municipalities, and telecommunications providers — with COIs issued within 24 hours.
          </p>
        </div>

        {/* Trust badges */}
        <div className="mt-14 flex flex-wrap gap-3">
          {TRUST_BADGES.map((b, i) => (
            <Reveal key={b} delay={i * 0.04}>
              <div className="flex items-center gap-2 bg-black/40 border border-white/10 px-4 py-2.5">
                <Award className="w-4 h-4 text-wcc-red" />
                <span className="mono text-[11px] uppercase tracking-widest">{b}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="w-6 h-6 text-wcc-red" />
              <h3 className="font-display text-2xl font-medium">Our Coverage Includes</h3>
            </div>
            <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
              {COVERAGE.map((c) => (
                <li key={c} className="flex items-start gap-2.5 text-white/75">
                  <span className="mt-2 h-1.5 w-1.5 bg-wcc-red shrink-0" />{c}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <HardHat className="w-6 h-6 text-wcc-red" />
              <h3 className="font-display text-2xl font-medium">Contractor Compliance</h3>
            </div>
            <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
              {COMPLIANCE.map((c) => (
                <li key={c} className="flex items-start gap-2.5 text-white/75">
                  <span className="mt-2 h-1.5 w-1.5 bg-wcc-red shrink-0" />{c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5 bg-black/30 border border-white/10 p-8">
            <Zap className="w-8 h-8 text-wcc-red shrink-0" />
            <p className="text-white/80 flex-1">
              Need our insurance certificates or prequalification package? We’ll provide the documentation
              needed to get your project moving.
            </p>
            <Link to="/resources" className="inline-flex items-center gap-2 bg-wcc-red text-white font-semibold px-6 py-3 rounded-sm hover:bg-wcc-red-deep transition-colors whitespace-nowrap" data-testid="prime-resources-btn">
              Contractor Resources <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section className="bg-wcc-paper py-16 border-y border-wcc-ink/10" data-testid="partners-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-gray text-center mb-8">
          Built to work alongside the industry’s primes
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {PARTNERS.map((p) => (
            <span key={p} className="font-display text-xl sm:text-2xl font-medium text-wcc-ink/40 hover:text-wcc-navy transition-colors">
              {p}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function CoverageMap() {
  // Approximate US grid — [row, col] positions loosely matching geography.
  // Each state is rendered as a rounded square tile with abbreviation.
  const GRID = [
    // row 0 — northern tier + Alaska tucked bottom-left
    { s: "WA", r: 0, c: 1 }, { s: "ID", r: 1, c: 2 }, { s: "MT", r: 0, c: 3 }, { s: "ND", r: 0, c: 4 }, { s: "MN", r: 0, c: 5 }, { s: "WI", r: 0, c: 6 }, { s: "MI", r: 0, c: 7 }, { s: "NY", r: 0, c: 8 }, { s: "VT", r: 0, c: 9 }, { s: "NH", r: 0, c: 10 }, { s: "ME", r: 0, c: 11 },
    { s: "OR", r: 1, c: 1 }, { s: "WY", r: 1, c: 3 }, { s: "SD", r: 1, c: 4 }, { s: "IA", r: 1, c: 5 }, { s: "IL", r: 1, c: 6 }, { s: "IN", r: 1, c: 7 }, { s: "OH", r: 1, c: 8 }, { s: "PA", r: 1, c: 9 }, { s: "NJ", r: 1, c: 10 }, { s: "CT", r: 1, c: 11 }, { s: "MA", r: 1, c: 12 },
    { s: "NV", r: 2, c: 1 }, { s: "UT", r: 2, c: 2 }, { s: "CO", r: 2, c: 3 }, { s: "NE", r: 2, c: 4 }, { s: "MO", r: 2, c: 5 }, { s: "KY", r: 2, c: 6 }, { s: "WV", r: 2, c: 7 }, { s: "VA", r: 2, c: 8 }, { s: "MD", r: 2, c: 9 }, { s: "DE", r: 2, c: 10 }, { s: "RI", r: 2, c: 11 },
    { s: "CA", r: 3, c: 0 }, { s: "AZ", r: 3, c: 2 }, { s: "NM", r: 3, c: 3 }, { s: "KS", r: 3, c: 4 }, { s: "AR", r: 3, c: 5 }, { s: "TN", r: 3, c: 6 }, { s: "NC", r: 3, c: 7 }, { s: "SC", r: 3, c: 8 },
    { s: "OK", r: 4, c: 4 }, { s: "LA", r: 4, c: 5 }, { s: "MS", r: 4, c: 6 }, { s: "AL", r: 4, c: 7 }, { s: "GA", r: 4, c: 8 },
    { s: "AK", r: 5, c: 0 }, { s: "HI", r: 5, c: 1 }, { s: "TX", r: 5, c: 4 }, { s: "FL", r: 5, c: 8 },
  ];

  const homeSet = new Set(HOME_STATES);
  const activeSet = new Set(ACTIVE_STATES);

  const CELL = 62;
  const GAP = 6;
  const COLS = 13;
  const ROWS = 6;
  const width = COLS * (CELL + GAP);
  const height = ROWS * (CELL + GAP);

  return (
    <section id="coverage" className="relative bg-wcc-navy text-white py-24 sm:py-32 overflow-hidden" data-testid="coverage-section">
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" aria-hidden="true"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow className="!text-[#c8341c]"><span style={{ color: "#c8341c" }}>06 — Nationwide</span></Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tighter leading-[0.95] text-white">
              <MaskLineInView>Crews ready to</MaskLineInView>
              <MaskLineInView delay={0.08}>mobilize where</MaskLineInView>
              <MaskLineInView delay={0.16}><span style={{ color: "#c8341c" }}>you build.</span></MaskLineInView>
            </h2>
            <Reveal delay={0.24}>
              <p className="mt-8 text-white/80 leading-relaxed max-w-md">
                Headquartered in Kentucky with recent and active projects across the eastern half of the country.
                Our fleet, crews, and back-office scale up quickly for multi-state builds and emergency response.
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-10 space-y-3">
                <LegendItem swatchColor="#c8341c" label="Home base" />
                <LegendItem swatchColor="rgba(200,52,28,0.6)" label="Recent / active projects" />
                <LegendItem swatchColor="rgba(255,255,255,0.12)" label="Available for mobilization" bordered />
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-sm">
                <Metric v={HOME_STATES.length + ACTIVE_STATES.length + "+"} l="States" />
                <Metric v="24/7" l="Response" />
                <Metric v="Nation-wide" l="Reach" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative">
                <svg
                  viewBox={`0 0 ${width} ${height}`}
                  className="w-full h-auto block"
                  data-testid="coverage-map"
                  aria-label="US coverage map"
                >
                  {GRID.map(({ s, r, c }) => {
                    const isHome = homeSet.has(s);
                    const isActive = activeSet.has(s);
                    const fill = isHome ? "#c8341c" : isActive ? "#c8341c99" : "rgba(255,255,255,0.09)";
                    const stroke = isHome ? "#c8341c" : isActive ? "#c8341c" : "rgba(255,255,255,0.22)";
                    const textFill = isHome || isActive ? "#ffffff" : "rgba(255,255,255,0.55)";
                    return (
                      <g key={s} data-testid={`state-${s}`} className="cursor-default">
                        <rect
                          x={c * (CELL + GAP)}
                          y={r * (CELL + GAP)}
                          width={CELL}
                          height={CELL}
                          rx="6"
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={isHome ? 2 : 1}
                        />
                        <text
                          x={c * (CELL + GAP) + CELL / 2}
                          y={r * (CELL + GAP) + CELL / 2 + 6}
                          textAnchor="middle"
                          fontSize="18"
                          fontWeight="600"
                          fill={textFill}
                          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                        >{s}</text>
                        {isHome && (
                          <circle cx={c * (CELL + GAP) + CELL - 10} cy={r * (CELL + GAP) + 10} r="4" fill="#ffffff">
                            <animate attributeName="opacity" values="1;0.35;1" dur="1.8s" repeatCount="indefinite" />
                          </circle>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function LegendItem({ swatchColor, label, bordered }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="inline-block w-4 h-4 rounded-sm"
        style={{ backgroundColor: swatchColor, ...(bordered ? { border: "1px solid rgba(255,255,255,0.25)" } : {}) }}
      />
      <span className="text-sm text-white/80">{label}</span>
    </div>
  );
}

function Metric({ v, l }) {
  return (
    <div>
      <p className="font-display text-3xl font-semibold text-white leading-none">{v}</p>
      <p className="mono text-[10px] uppercase tracking-widest text-white/50 mt-1">{l}</p>
    </div>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "Prequalification Request", message: "", hp: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Please enter your name.");
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. Our team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "Prequalification Request", message: "", hp: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-white py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <Eyebrow>06 — Get In Touch</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter uppercase leading-none text-wcc-ink">
            Let’s build the
            <br />
            <span className="text-wcc-red">next mile.</span>
          </h2>
          <p className="mt-6 text-wcc-ink/70 leading-relaxed max-w-md">
            Request our capability statement, insurance certificates, or discuss an upcoming underground fiber
            project. We mobilize fast.
          </p>
          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 grid place-items-center bg-wcc-navy text-white shrink-0"><Map className="w-5 h-5" /></div>
              <div className="min-w-0">
                <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Headquarters</p>
                <p className="text-wcc-ink font-medium break-words">{COMPANY.address}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 grid place-items-center bg-wcc-red text-white shrink-0"><Cable className="w-5 h-5" /></div>
              <div className="min-w-0">
                <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Email</p>
                <a href={`mailto:${COMPANY.email}`} className="text-wcc-ink font-medium hover:text-wcc-red transition-colors break-all">{COMPANY.email}</a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-wcc-paper border border-wcc-ink/10 p-8 sm:p-10" data-testid="contact-form">
          <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={form.hp} onChange={set("hp")} style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name" testid="contact-name" value={form.name} onChange={set("name")} required />
            <Field label="Company" testid="contact-company" value={form.company} onChange={set("company")} />
            <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={set("email")} />
            <Field label="Phone" testid="contact-phone" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="mt-5">
            <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Subject</label>
            <input value={form.subject} onChange={set("subject")} data-testid="contact-subject"
              className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
          </div>
          <div className="mt-5">
            <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Message</label>
            <textarea value={form.message} onChange={set("message")} rows={4} data-testid="contact-message"
              className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink resize-none transition-colors" />
          </div>
          <button type="submit" disabled={loading} data-testid="contact-submit"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-wcc-red text-white font-semibold px-6 py-4 rounded-sm hover:bg-wcc-red-deep disabled:opacity-60 transition-colors">
            {loading ? "Sending…" : <>Send Message <Send className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, testid, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{label}{required && <span className="text-wcc-red"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} required={required}
        className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
    </div>
  );
}

export default function Home() {
  return (
    <div className="home-blue">
      <Hero />
      <Overview />
      <Services />
      <Equipment />
      <Why />
      <PrimeReady />
      <Partners />
      <CoverageMap />
      <Contact />
    </div>
  );
}
